import React, { useCallback, useEffect, useMemo, useState, memo, ReactNode } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { Table, Button, Tooltip } from '@wmgtech/legato'

import { ConditionRender } from 'components'
import { FormInput } from 'components/FormWithGroup'
import { isUniqueFieldValue } from 'modules/Labels/utils'

import { ColumnsType } from '@wmgtech/legato/build/components/Table/types'
import { TAlertColorType } from '@wmgtech/legato/build/components/Alert/types'
import { TableFormListProps, IType, TForm, ITypeForm } from './TableFormList.props'

import './TableFormList.scss'

const TableFormList = memo(
  <T extends IType>({
    entity,
    defaultList,
    onHandleChangeForm,
    fieldName,
    setShowAlert,
    isTableHidden
  }: TableFormListProps<T>) => {
    const [editedId, setEditedId] = useState<string | null>(null)

    const defaultValues = useMemo(
      (): ITypeForm<TForm>[] =>
        isTableHidden
          ? []
          : defaultList.map((item) => ({ ...item, saved: false, fieldId: item?.id } as ITypeForm<TForm>)),
      [defaultList, isTableHidden]
    )

    const {
      control,
      reset,
      getValues,
      formState: {
        errors: { list: listErrors }
      }
    } = useForm<{ list: ITypeForm<TForm>[] }>({
      defaultValues: { list: defaultValues },
      mode: 'all'
    })

    const { fields, append } = useFieldArray({ name: 'list', control })
    const hasNewField = useMemo(() => !!fields.find((field) => !field.fieldId), [fields])

    const onEditClick = useCallback(
      (id: string) => () => {
        if (!id || hasNewField || editedId !== id) reset()
        setEditedId(id)
      },
      [reset, hasNewField, editedId]
    )

    const onAddUnit = useCallback(() => {
      reset()
      setEditedId(`${fields.length}`)

      append({ [fieldName as 'name']: '' } as ITypeForm<TForm>)
    }, [append, setEditedId, fields, reset, fieldName])

    const onShowAlert = useCallback(
      (message: string | ReactNode, title: string = 'Success', type: TAlertColorType = 'success') => {
        setShowAlert({ message, title, type })
      },
      [setShowAlert]
    )

    const onShowError = useCallback(
      (isRequired: boolean) => {
        const message = isRequired ? (
          <>
            <b>{entity} Name</b> is required.
          </>
        ) : (
          <>
            <b>{entity} Name</b> should be unique.
          </>
        )

        onShowAlert(message, 'Error', 'danger')
      },
      [entity, onShowAlert]
    )

    const onSaveHandler = useCallback(
      (modifiedValues: ITypeForm<TForm>) => {
        onHandleChangeForm(modifiedValues).then(() => {
          onShowAlert(
            <>
              Your {entity.toLowerCase()} <b>{modifiedValues[fieldName]}</b> has been successfully{' '}
              {modifiedValues.id ? 'updated' : 'added'}.
            </>
          )
          onEditClick('')()
        })
      },
      [entity, fieldName, onHandleChangeForm, onShowAlert, onEditClick]
    )

    const onSave = useCallback(
      (idx: number) => () => {
        const values = getValues()?.list as ITypeForm<TForm>[]
        const modifiedUnit = values[idx]
        if (
          !modifiedUnit[fieldName] ||
          !isUniqueFieldValue(modifiedUnit, (isTableHidden ? defaultList : values) as ITypeForm<TForm>[], fieldName)
        ) {
          onShowError(!modifiedUnit[fieldName])
        } else {
          onSaveHandler(modifiedUnit)
        }
      },
      [getValues, onShowError, onSaveHandler, defaultList, isTableHidden, fieldName]
    )

    const dataListRows = useMemo(
      () =>
        fields.map((field: ITypeForm<TForm>, idx: number) => ({
          ...field,
          isEditMode: `${idx}` === editedId,
          error: listErrors?.[idx]
        })),
      [fields, editedId, listErrors]
    )

    const onRow = useCallback(
      (_, idx) => {
        if (editedId === `${idx}`) {
          return { 'data-edited': 'true', id: idx.toString() }
        }
        return { id: idx.toString() }
      },
      [editedId]
    )

    const columns: ColumnsType<unknown>[] = [
      {
        dataIndex: fieldName,
        key: fieldName,
        title: 'Name',
        sortable: false,
        align: 'left',
        render: (value, dataValues: any, idx: number) => (
          <ConditionRender
            condition={!!dataValues.isEditMode}
            component={
              <FormInput
                className="form-list__input"
                fieldName={`list.${idx}.${fieldName}`}
                control={control}
                {...fields[idx]}
                label={''}
                validationIconPlacement="input"
                placeholder={`Enter New ${entity}`}
              />
            }
            secondComponent={<div className="form-list__value">{value}</div>}
          />
        )
      },
      {
        dataIndex: '',
        key: 'edit',
        title: '',
        sortable: false,
        align: 'right',
        width: 60,
        render: (value, dataValues: any, idx: number) => (
          <ConditionRender
            condition={!!dataValues.isEditMode}
            component={
              <div className="form-list__action">
                <Tooltip message="Cancel" colorType="dark">
                  <Button
                    className="form-list__action--cancel"
                    icon="xmark"
                    colorType="secondary-black"
                    containerStyle="link"
                    onClick={onEditClick('')}
                  />
                </Tooltip>
                <Tooltip message={`Save ${entity}`} colorType="dark">
                  <Button
                    className="form-list__action--save"
                    icon="check"
                    colorType="secondary-black"
                    containerStyle="link"
                    onClick={onSave(idx)}
                  />
                </Tooltip>
              </div>
            }
            secondComponent={
              <Tooltip message={`Edit ${entity}`} colorType="dark">
                <Button
                  icon="pencil"
                  colorType="secondary-black"
                  containerStyle="link"
                  onClick={onEditClick(`${idx}`)}
                />
              </Tooltip>
            }
          />
        )
      }
    ]

    useEffect(() => {
      reset({ list: defaultValues })
    }, [reset, defaultValues])

    return (
      <div className="form-list">
        <ConditionRender
          condition={!!dataListRows.length}
          component={
            <Table
              className="form-list__table"
              rowKey={(record: any) => record.id}
              columns={columns}
              data={dataListRows}
              pagination={false}
              onRow={onRow}
              transparent
              condensed
            />
          }
        />
        <Button
          label={`Add ${entity}`}
          colorType="secondary-black"
          containerStyle="outline"
          secondaryIcon="circle-plus"
          onClick={onAddUnit}
          disabled={hasNewField}
        />
      </div>
    )
  }
)

export default TableFormList
